import { QdsNativeField, type FieldValidity } from './native-field.js';

export interface TemporalValidity extends FieldValidity {
  rangeUnderflow: boolean;
  rangeOverflow: boolean;
  stepMismatch: boolean;
  badInput: boolean;
}

/** Native local time editing shared by the time and local date-time controls. */
export abstract class QdsNativeTemporalField extends QdsNativeField {
  static override get observedAttributes(): string[] {
    return [...super.observedAttributes, 'value', 'min', 'max', 'step', 'locale', 'form'];
  }
  protected abstract readonly _inputType: 'time' | 'datetime-local';
  private _value = '';
  private _normalizer?: HTMLInputElement;
  private _forceValue = false;

  protected override get _upgradeNames(): string[] { return ['value', 'defaultValue', 'min', 'max', 'step', 'locale']; }
  get value(): string { return this._value; }
  set value(value: string) { this._dirty = true; this._assign(value); this._sync(); }
  get defaultValue(): string { return this._normalize(this.getAttribute('value') || ''); }
  set defaultValue(value: string) { this.setAttribute('value', String(value ?? '')); }
  get min(): string { return this._normalize(this.getAttribute('min') || ''); }
  set min(value: string) { this.setAttribute('min', String(value ?? '')); }
  get max(): string { return this._normalize(this.getAttribute('max') || ''); }
  set max(value: string) { this.setAttribute('max', String(value ?? '')); }
  get step(): number | 'any' {
    const text = this.getAttribute('step');
    if (text === 'any') return 'any';
    const value = Number(text);
    return Number.isFinite(value) && value > 0 ? value : 60;
  }
  set step(value: number | 'any') { this.setAttribute('step', String(value)); }
  get locale(): string { return this.getAttribute('locale') || this.closest('[lang]')?.getAttribute('lang') || this.ownerDocument.documentElement.lang || ''; }
  set locale(value: string) { this.setAttribute('locale', value ?? ''); }
  override get validity(): TemporalValidity {
    return { rangeUnderflow: false, rangeOverflow: false, stepMismatch: false, badInput: false, ...super.validity };
  }
  /** Localized presentation only; the model has no UTC offset or timezone. */
  get formattedValue(): string {
    if (!this.value) return '';
    const dateTime = this._inputType === 'datetime-local';
    const date = new Date(`${dateTime ? '' : '2000-01-01T'}${this.value}Z`);
    if (!Number.isFinite(date.getTime())) return this.value;
    const time = this.value.split('T').pop()!;
    const fraction = time.split('.')[1]?.length;
    const options: Intl.DateTimeFormatOptions = {
      timeZone: 'UTC', hour: 'numeric', minute: '2-digit',
      ...(dateTime ? { year: 'numeric', month: 'short', day: 'numeric' } as const : {}),
      ...(time.split(':').length > 2 ? { second: '2-digit' } as const : {}),
      ...(fraction ? { fractionalSecondDigits: Math.min(3, fraction) as 1 | 2 | 3 } : {}),
    };
    try { return new Intl.DateTimeFormat(this.locale || undefined, options).format(date); }
    catch { return this.value; }
  }
  private _normalize(value: unknown): string {
    if (typeof value !== 'string') return '';
    const input = this._normalizer ??= this.ownerDocument.createElement('input');
    input.type = this._inputType;
    input.value = value;
    return input.value;
  }
  private _assign(value: unknown): void { this._value = this._normalize(value); this._forceValue = true; }
  override attributeChangedCallback(): void {
    if (!this._dirty) this._assign(this.defaultValue);
    super.attributeChangedCallback();
  }
  protected _render(): void {
    if (!this._dirty) this._assign(this.defaultValue);
    this.shadowRoot!.innerHTML = `<style>${this._styles}
      :host { display: inline-block; width: 100%; }
      input.qds-control-surface { display: block; width: 100%; min-width: 0; min-height: 44px; padding: 10px 12px; }
      .qds-error { color: var(--theme-text-primary); border-inline-start: 3px solid var(--theme-error); padding-inline-start: .5rem; }
      .qds-temporal-value { margin: 4px 0; color: var(--theme-text-secondary); font-size: .875rem; overflow-wrap: anywhere; }
    </style><div part="container"><label class="qds-label" part="label" for="${this._id}"><span class="label-text"></span><span class="qds-required-marker" aria-hidden="true">*</span></label><input type="${this._inputType}" id="${this._id}" class="qds-control-surface" part="input"><p class="qds-temporal-value" part="value" id="${this._id}-value"></p><div class="qds-feedback"><div class="qds-hint" part="hint" id="${this._id}-hint"></div><div class="qds-error" part="error" id="${this._id}-error" aria-live="polite"></div></div></div>`;
    this._control = this.shadowRoot!.querySelector('input')!;
  }
  protected _syncControl(): void {
    const input = this._control as HTMLInputElement;
    const type = this.readonly ? 'text' : this._inputType;
    if (input.type !== type) {
      const focused = this.shadowRoot!.activeElement === input;
      input.readOnly = this.readonly; input.type = type; this._forceValue = true;
      if (focused) input.focus();
    }
    input.min = this.min; input.max = this.max; input.step = String(this.step);
    // The native default attribute supplies the step base when min is absent.
    if (input.getAttribute('value') !== this.defaultValue) input.defaultValue = this.defaultValue;
    // Leave native partial segmented edits alone. Owner/reset/restore writes
    // intentionally clear an incomplete editor even when its value is empty.
    if (this._forceValue || input.value !== this.value) input.value = this.value;
    this._forceValue = false;
    const output = this.shadowRoot!.querySelector('.qds-temporal-value') as HTMLElement;
    output.textContent = this.formattedValue;
    output.hidden = !this.value;
  }
  protected override _counterDescription(): string { return this.value ? `${this._id}-value` : ''; }
  private _accept(event: Event, type: 'input' | 'change'): void {
    event.stopPropagation();
    if (this.disabled || this.readonly) { this._forceValue = true; this._sync(); return; }
    this._dirty = true;
    this._value = this._control!.value;
    if (this.validateOn === 'input') this._showError = true;
    this._sync();
    this._emit(type, { value: this.value });
  }
  private _onInput = (event: Event): void => this._accept(event, 'input');
  private _onChange = (event: Event): void => this._accept(event, 'change');
  protected _bind(): void {
    this._control!.addEventListener('input', this._onInput);
    this._control!.addEventListener('change', this._onChange);
    this._control!.addEventListener('blur', this._onBlur);
  }
  protected _unbind(): void {
    this._control?.removeEventListener('input', this._onInput);
    this._control?.removeEventListener('change', this._onChange);
    this._control?.removeEventListener('blur', this._onBlur);
  }
  protected _formValue(): string { return this.value; }
  protected _formState(): string { return this.value; }
  protected _reset(): void { this._assign(this.defaultValue); }
  protected _restore(state: string): void { this._assign(state); }
  protected _constraints(): { flags: Partial<TemporalValidity>; message: string } {
    const input = this._control as HTMLInputElement;
    const { valueMissing, rangeUnderflow, rangeOverflow, stepMismatch, badInput } = input.validity;
    return { flags: { valueMissing, rangeUnderflow, rangeOverflow, stepMismatch, badInput }, message: input.validationMessage };
  }
}
